import { useCallback, useEffect, useRef, useState } from 'react';
import './scratchcard.css';
import couponImg from '../../assets/gift-pack/coupon.png';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <rect x="8" y="8" width="12" height="12" rx="2" stroke="#6c4ce8" strokeWidth="1.8" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" stroke="#6c4ce8" strokeWidth="1.8" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l5 5L19 7" stroke="#17b85a" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SparkleIcon({ className, color = '#ff9f2e' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M12 2c0 5.2-2.8 8-8 8 5.2 0 8 2.8 8 8 0-5.2 2.8-8 8-8-5.2 0-8-2.8-8-8Z" fill={color} />
    </svg>
  );
}

function HandIcon({ className }) {
  return (
    <svg className={className} width="34" height="34" viewBox="0 0 24 24" fill="none">
      <path
        d="M8 13V6a1.5 1.5 0 0 1 3 0v5M11 11V4.5a1.5 1.5 0 0 1 3 0V11M14 11.5V6a1.5 1.5 0 0 1 3 0v7"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path
        d="M8 12.5 6.6 11a1.4 1.4 0 0 0-2.2 1.7L7 17.5A5 5 0 0 0 11.5 20H14a5 5 0 0 0 5-5v-3.5"
        stroke="#ffffff"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const CONFETTI = [
  { color: '#17b8a6', dx: -70, dy: -55, rot: 130, delay: 0 },
  { color: '#ff3d81', dx: 65, dy: -70, rot: -110, delay: 0.05 },
  { color: '#ffb238', dx: -40, dy: -85, rot: 190, delay: 0.1 },
  { color: '#6c4ce8', dx: 75, dy: -25, rot: -70, delay: 0.02 },
  { color: '#17b8a6', dx: -80, dy: -10, rot: 50, delay: 0.14 },
  { color: '#ffb238', dx: 48, dy: -88, rot: -150, delay: 0.08 },
];

const REVEAL_THRESHOLD = 0.35;
const BRUSH_RADIUS = 32;
const SAMPLE_STEP = 6;

// Formats a reward's wallet_credit exactly as the admin configured it —
// whole rupees with no decimals, fractional amounts with two.
function formatWalletAmount(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `₹${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

export default function ScratchCard({ onBack, onContinue, reward, isLoading = false }) {
  const discount = reward?.discount;
  const description = reward?.description;
  const code = reward?.code;
  const walletAmount = formatWalletAmount(reward?.wallet_credit);

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const [revealed, setRevealed] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [copied, setCopied] = useState(false);

  const drawFoil = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const { width, height } = container.getBoundingClientRect();
    if (width === 0 || height === 0) {
      requestAnimationFrame(drawFoil);
      return;
    }
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;

    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.globalCompositeOperation = 'source-over';

    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, '#c3b2fa');
    grad.addColorStop(0.5, '#8b6cf0');
    grad.addColorStop(1, '#5c34d1');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.16)';
    ctx.lineWidth = 9;
    for (let x = -height; x < width; x += 24) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x + height, height);
      ctx.stroke();
    }

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.font = "800 15px 'Baloo 2', sans-serif";
    ctx.fillText('✦ SCRATCH HERE ✦', width / 2, height / 2 - 12);
    ctx.font = '600 11px sans-serif';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.fillText('use your finger', width / 2, height / 2 + 12);
  }, []);

  useEffect(() => {
    drawFoil();
    window.addEventListener('resize', drawFoil);
    return () => window.removeEventListener('resize', drawFoil);
  }, [drawFoil]);

  const getPoint = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches && e.touches[0];
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const scratchTo = (point, from) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.globalAlpha = 1;
    ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = BRUSH_RADIUS * 2;
    ctx.beginPath();
    ctx.moveTo((from || point).x, (from || point).y);
    ctx.lineTo(point.x, point.y);
    ctx.stroke();
  };

  const checkRevealed = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    const data = ctx.getImageData(0, 0, width, height).data;
    let cleared = 0;
    let total = 0;
    for (let i = 3; i < data.length; i += 4 * SAMPLE_STEP) {
      total++;
      if (data[i] < 40) cleared++;
    }
    if (total > 0 && cleared / total > REVEAL_THRESHOLD) {
      setRevealed(true);
    }
  }, []);

  const handleDown = (e) => {
    if (revealed) return;
    isDrawingRef.current = true;
    if (!hasStarted) setHasStarted(true);
    const p = getPoint(e);
    scratchTo(p, null);
    lastPointRef.current = p;
  };

  const handleMove = (e) => {
    if (!isDrawingRef.current || revealed) return;
    const p = getPoint(e);
    scratchTo(p, lastPointRef.current);
    lastPointRef.current = p;
  };

  const handleUp = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPointRef.current = null;
    checkRevealed();
  };

  const handleCopy = () => {
    if (!revealed || !code) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // Loading and no-reward states never show the old hardcoded prize copy —
  // only a real Laravel reward (passed in via `reward`) reaches the scratch UI below.
  if (isLoading || !reward) {
    return (
      <div className="scratch-screen">
        <div className="scratch-frame">
          <div className="scratch-top">
            <button type="button" className="scratch-back-btn" aria-label="Go back" onClick={onBack}>
              <ArrowLeftIcon />
            </button>
            <h1 className="scratch-title">Your Reward</h1>
            <span className="scratch-top-spacer" />
          </div>
          <p className="scratch-subtitle">
            {isLoading ? 'Loading your reward…' : 'No reward available right now.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="scratch-screen">
      <div className="scratch-frame">
        <div className="scratch-top">
          <button type="button" className="scratch-back-btn" aria-label="Go back" onClick={onBack}>
            <ArrowLeftIcon />
          </button>
          <h1 className="scratch-title">Your Reward</h1>
          <span className="scratch-top-spacer" />
        </div>

        <p className="scratch-subtitle">
          {revealed ? 'Nice! Here’s your surprise 🎉' : 'Scratch the card to reveal your surprise!'}
        </p>

        <div className="scratch-stage">
          {/* <SparkleIcon className="scratch-sparkle scratch-sparkle-1" color="#ffb238" />
          <SparkleIcon className="scratch-sparkle scratch-sparkle-2" color="#6c4ce8" />
          <SparkleIcon className="scratch-sparkle scratch-sparkle-3" color="#17b8a6" /> */}

          <div className="scratch-card" ref={containerRef}>
            <div className="scratch-prize">
              <img src={couponImg} alt="" className="scratch-prize-ticket" />
              <span className="scratch-prize-discount">{discount}</span>
              {walletAmount && <span className="scratch-prize-amount">{walletAmount}</span>}
              <span className="scratch-prize-desc">{description}</span>
              <span className="scratch-prize-code-wrap">
                <button
                  type="button"
                  className="scratch-prize-code"
                  onClick={handleCopy}
                  disabled={!revealed || !code}
                >
                  <span>{code}</span>
                  <span className="scratch-prize-copy-icon">{copied ? <CheckIcon /> : <CopyIcon />}</span>
                </button>
                {copied && <span className="scratch-copied-toast">Copied!</span>}
              </span>
            </div>

            <canvas
              ref={canvasRef}
              className={'scratch-canvas' + (revealed ? ' scratch-canvas-cleared' : '')}
              onMouseDown={handleDown}
              onMouseMove={handleMove}
              onMouseUp={handleUp}
              onMouseLeave={handleUp}
              onTouchStart={handleDown}
              onTouchMove={handleMove}
              onTouchEnd={handleUp}
            />

            {!hasStarted && !revealed && (
              <div className="scratch-hint">
                <HandIcon className="scratch-hint-hand" />
              </div>
            )}
          </div>

          {revealed &&
            CONFETTI.map((c, i) => (
              <span
                key={i}
                className="scratch-confetti"
                style={{
                  background: c.color,
                  '--dx': `${c.dx}px`,
                  '--dy': `${c.dy}px`,
                  '--rot': `${c.rot}deg`,
                  animationDelay: `${c.delay}s`,
                }}
              />
            ))}
        </div>

        {revealed && walletAmount && (
          <p className="scratch-wallet-note">
            {walletAmount} will be added to your wallet soon.
          </p>
        )}

        <button
          type="button"
          className={'scratch-continue-btn' + (revealed ? ' scratch-continue-btn-visible' : '')}
          onClick={onContinue}
          disabled={!revealed}
        >
          Continue
        </button>
      </div>
    </div>
  );
}
